import { Fragment, useState, type ReactElement } from 'react';
import {
  contractStatusClass,
  formatContractStatus,
  formatContractType,
  type ContractDetail
} from '../../domain/contracts';
import { activityFromNotes } from '../../domain/activityLog';
import { ActivityTable } from '../activity/ActivityTable';
import { LiveDoiPanel } from './LiveDoiPanel';
import { LeaseObligationsPanel } from './LeaseObligationsPanel';
import { OfftakePricingPanel } from './OfftakePricingPanel';

interface ContractDetailSidePanelProps {
  item: ContractDetail | null;
  loading?: boolean;
  error?: string;
  variant?: 'panel' | 'page';
  onClose: () => void;
  onBack?: () => void;
  onOpenFullPage?: (id: string) => void;
  canActivate: boolean;
  onActivate?: () => void | Promise<void>;
  /** Number from the selected grid row while the detail request is in flight. */
  titleHint?: string;
  authFetch?: (url: string, init?: RequestInit) => Promise<Response>;
  canWriteDoi?: boolean;
  canUpdate?: boolean;
  onPostAssignment?: () => void | Promise<void>;
  onOpenRelatedLease?: (id: string, asOf: string) => void;
  asOfDefault?: string;
}

export function ContractDetailSidePanel({
  item,
  loading = false,
  error = '',
  variant = 'panel',
  onClose,
  onBack,
  onOpenFullPage,
  canActivate,
  onActivate,
  titleHint,
  authFetch,
  canWriteDoi = false,
  canUpdate = false,
  onPostAssignment,
  onOpenRelatedLease,
  asOfDefault
}: ContractDetailSidePanelProps): ReactElement {
  const isPage = variant === 'page';
  const isDraft = item?.status.toLowerCase() === 'draft';
  const isAssignment = item?.type === 'AssignmentOfOilAndGasInterests';
  const isLease = item?.type === 'OilGasLease';
  const isJoa = item?.type === 'JointOperatingAgreement';
  const isOfftake = item?.type === 'MarketingOfftake';
  const [asOf, setAsOf] = useState(() => {
    if (asOfDefault) {
      return asOfDefault;
    }
    const now = new Date();
    return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-01`;
  });

  return (
    <aside
      className={`side-panel${isPage ? ' side-panel--page' : ''}`}
      aria-label="Contract details"
    >
      <div className="side-panel-header">
        <div className="side-panel-title-row">
          {isPage && onBack && (
            <button
              type="button"
              className="secondary panel-back-btn"
              data-testid="contract-back"
              onClick={onBack}
            >
              ← Back to list
            </button>
          )}
          <h3>{item?.number ?? titleHint ?? 'Contract'}</h3>
        </div>
        <div className="side-panel-actions">
          {!isPage && item && onOpenFullPage && (
            <button type="button" className="secondary" onClick={() => onOpenFullPage(item.id)}>
              Open full page
            </button>
          )}
          {!isPage && (
            <button type="button" className="icon-close" onClick={onClose} aria-label="Close panel">
              ×
            </button>
          )}
        </div>
      </div>
      <div className="side-panel-body">
        {error && <p className="error">{error}</p>}
        {loading && !item && <p className="hint">Loading…</p>}
        {item && (
          <>
            <dl className="detail-list">
              <dt>Number</dt>
              <dd className="mono-id">{item.number}</dd>
              <dt>Status</dt>
              <dd>
                <span className={contractStatusClass(item.status)}>{formatContractStatus(item.status)}</span>
              </dd>
              <dt>Title</dt>
              <dd>{item.title}</dd>
              <dt>Type</dt>
              <dd>{formatContractType(item.type)}</dd>
              <dt>Region</dt>
              <dd>{item.operatingRegion}</dd>
              <dt>Effective</dt>
              <dd>{item.effectiveFrom || '—'}</dd>
              <dt>End date</dt>
              <dd>{item.effectiveTo?.trim() || '—'}</dd>
              <dt>Royalty</dt>
              <dd>{item.royalty ?? '—'}</dd>
              <dt>Paid up</dt>
              <dd>{item.paidUp ? 'Yes' : 'No'}</dd>
              <dt>Primary term</dt>
              <dd>{item.primaryTermMonths != null ? `${item.primaryTermMonths} months` : '—'}</dd>
              <dt>Recording</dt>
              <dd>{item.recordingNumber?.trim() || '—'}</dd>
              <dt>Land file</dt>
              <dd>{item.landFileId?.trim() || '—'}</dd>
              <dt>Citation is operator</dt>
              <dd>{item.citationIsOperator ? 'Yes' : 'No'}</dd>
            </dl>
            {item.notes?.trim() ? (
              <section className="nested-card" aria-label="Notes">
                <h4 className="form-section-title">Notes</h4>
                <p className="multiline">{item.notes}</p>
              </section>
            ) : null}
            <section className="nested-card" aria-label="Parties">
              <h4 className="form-section-title">Parties</h4>
              {item.parties.length === 0 ? (
                <p className="hint">None</p>
              ) : (
                <dl className="detail-list">
                  {item.parties.map((p) => (
                    <Fragment key={`${p.partyId}-${p.role}`}>
                      <dt>{p.role}</dt>
                      <dd>{p.legalName ?? p.partyId}</dd>
                    </Fragment>
                  ))}
                </dl>
              )}
            </section>
            <section className="nested-card" aria-label="Wells">
              <h4 className="form-section-title">Wells</h4>
              {item.wells.length === 0 ? (
                <p className="hint">None</p>
              ) : (
                <dl className="detail-list">
                  {item.wells.map((w) => (
                    <Fragment key={w.wellId}>
                      <dt>API</dt>
                      <dd>
                        <span className="mono-id">{w.apiNumber}</span> {w.name}
                      </dd>
                    </Fragment>
                  ))}
                </dl>
              )}
            </section>
            {isAssignment && item.assignment && (
              <section className="nested-card" aria-label="Assignment">
                <h4 className="form-section-title">Assignment</h4>
                <dl className="detail-list">
                  <dt>Related lease</dt>
                  <dd className="mono-id">{item.assignment.relatedContractNumber ?? item.assignment.relatedContractId}</dd>
                  <dt>Instrument</dt>
                  <dd>{item.assignment.instrumentNumber?.trim() || item.recordingNumber?.trim() || '—'}</dd>
                  <dt>Recorded</dt>
                  <dd>{item.assignment.recordedDate?.trim() || '—'}</dd>
                  <dt>County / state</dt>
                  <dd>
                    {[item.assignment.county, item.assignment.state].filter(Boolean).join(', ') || '—'}
                  </dd>
                </dl>
                <table className="po-lines" data-testid="assignment-lines">
                  <thead>
                    <tr>
                      <th>Assignor</th>
                      <th>Assignee</th>
                      <th>Class</th>
                      <th>WI</th>
                      <th>NRI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item.assignment.lines.map((line, i) => (
                      <tr key={`${line.assignorOwnerId}-${line.assigneeOwnerId}-${i}`}>
                        <td>{line.assignorName ?? line.assignorOwnerId}</td>
                        <td>{line.assigneeName ?? line.assigneeOwnerId}</td>
                        <td>{line.interestClass}</td>
                        <td className="mono-id">{line.assignedWi}</td>
                        <td className="mono-id">{line.assignedNri}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {item.status.toLowerCase() === 'active' && item.assignment.relatedContractId && onOpenRelatedLease && (
                  <p className="hint">
                    <button
                      type="button"
                      className="secondary"
                      data-testid="assignment-open-doi"
                      onClick={() =>
                        onOpenRelatedLease(item.assignment!.relatedContractId, item.effectiveFrom)
                      }
                    >
                      DOI as of effective date
                    </button>
                  </p>
                )}
              </section>
            )}
            {authFetch && isLease && (
              <LeaseObligationsPanel contractId={item.id} authFetch={authFetch} canUpdate={canUpdate} />
            )}
            {authFetch && isOfftake && <OfftakePricingPanel contractId={item.id} authFetch={authFetch} />}
            {isJoa && item.joa && (
              <section className="nested-card" aria-label="JOA header" data-testid="joa-header">
                <h4 className="form-section-title">Joint operating agreement</h4>
                <p className="hint" data-testid="joa-jib-banner">
                  JIB and COPAS overhead are not calculated on this header. Rates are stored only.
                </p>
                <dl className="detail-list">
                  <dt>Operator</dt>
                  <dd>{item.joa.operatorName ?? item.joa.operatorPartyId}</dd>
                  <dt>Operated by us</dt>
                  <dd>{item.joa.operatedByUs ? 'Yes' : 'No'}</dd>
                  <dt>COPAS vintage</dt>
                  <dd>{item.joa.copasVintage}</dd>
                  <dt>COPAS note</dt>
                  <dd>{item.joa.copasNote?.trim() || '—'}</dd>
                  <dt>Related lease</dt>
                  <dd className="mono-id">
                    {item.joa.relatedContractNumber ?? item.joa.relatedContractId ?? '—'}
                  </dd>
                  <dt>Drilling overhead</dt>
                  <dd>
                    {item.joa.drillingOverhead != null
                      ? `${item.joa.drillingOverhead}${item.joa.overheadUnit ? ` ${item.joa.overheadUnit}` : ''}`
                      : '—'}
                  </dd>
                  <dt>Producing overhead</dt>
                  <dd>
                    {item.joa.producingOverhead != null
                      ? `${item.joa.producingOverhead}${item.joa.overheadUnit ? ` ${item.joa.overheadUnit}` : ''}`
                      : '—'}
                  </dd>
                  <dt>Parties</dt>
                  <dd>{item.joa.partiesDisplay?.trim() || '—'}</dd>
                </dl>
                <h4 className="form-section-title">Exhibit A</h4>
                {item.joa.exhibitA.length === 0 ? (
                  <p className="hint">None</p>
                ) : (
                  <table className="po-lines" data-testid="joa-exhibit-a">
                    <thead>
                      <tr>
                        <th>API</th>
                        <th>Name</th>
                        <th>Included</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.joa.exhibitA.map((well) => (
                        <tr key={well.wellId}>
                          <td className="mono-id">{well.apiNumber ?? well.wellId}</td>
                          <td>{well.name ?? '—'}</td>
                          <td>{well.included ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            )}
            {authFetch && isLease && item.status.toLowerCase() === 'active' && (
              <section className="nested-card" aria-label="Live DOI">
                <label className="grid">
                  As-of
                  <input type="date" data-testid="doi-asof" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
                </label>
                <LiveDoiPanel
                  authFetch={authFetch}
                  contractId={item.id}
                  asOf={asOf}
                  canWrite={canWriteDoi}
                  compact
                />
              </section>
            )}
            <section className="nested-card" aria-label="Tracts">
              <h4 className="form-section-title">Tracts</h4>
              {item.tracts.length === 0 ? (
                <p className="hint">None</p>
              ) : (
                <dl className="detail-list">
                  {item.tracts.map((t) => (
                    <Fragment key={t.tractId}>
                      <dt>Tract</dt>
                      <dd>{t.name}</dd>
                    </Fragment>
                  ))}
                </dl>
              )}
            </section>
            <div className="panel-actions">
              <ActivityTable testId="contract-activity" rows={activityFromNotes(item.activity)} />
              {canActivate && isDraft && (isLease || isJoa) && onActivate && (
                <div className="row">
                  <button type="button" data-testid="contract-activate" onClick={() => void onActivate()}>
                    Activate
                  </button>
                </div>
              )}
              {canWriteDoi && isDraft && isAssignment && onPostAssignment && (
                <div className="row">
                  <button type="button" data-testid="assignment-post" onClick={() => void onPostAssignment()}>
                    Post assignment
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
